// GoldSrc StudioModel (.mdl, version 10) parser — Half-Life / CS 1.6 models.
// Pure data extraction (no three.js). Bones are kept in their reference (bind)
// pose; `sampleBoneLocals` poses them from a sequence frame for animation.

export interface MdlBone {
  name: string
  parent: number
  pos: [number, number, number] // bind position
  rot: [number, number, number] // bind euler (radians)
  scale: [number, number, number, number, number, number]
}

export interface MdlTexture {
  name: string
  width: number
  height: number
  rgba: Uint8Array
  transparent: boolean
}

export interface MdlTriVert {
  vert: number
  s: number
  t: number
}

export interface MdlMesh {
  skinRef: number
  tris: MdlTriVert[] // flat list, length is a multiple of 3
}

export interface MdlModel {
  vertices: Float32Array // numVerts * 3, bone-local space
  vertBones: Uint8Array // bone index per vertex
  meshes: MdlMesh[]
}

export interface MdlSequence {
  label: string
  fps: number
  numFrames: number
  animIndex: number // file offset of the mstudioanim array
  seqGroup: number
  numBlends: number
  blendStart: number
  blendEnd: number
}

export interface BoneLocal {
  pos: [number, number, number]
  rot: [number, number, number]
}

export interface MdlAttachment {
  bone: number
  org: [number, number, number] // offset from the bone (e.g. muzzle = attachment 0)
}

export interface Mdl {
  bones: MdlBone[]
  models: MdlModel[]
  textures: MdlTexture[]
  skinRefs: number[]
  sequences: MdlSequence[]
  attachments: MdlAttachment[]
  view: DataView // retained for animation sampling
}

const STUDIO_NF_MASKED = 0x0040

function readStr(dv: DataView, off: number, max: number): string {
  let s = ''
  for (let i = 0; i < max; i++) {
    const c = dv.getUint8(off + i)
    if (c === 0) break
    s += String.fromCharCode(c)
  }
  return s
}

function decodeTexture(
  dv: DataView,
  name: string,
  w: number,
  h: number,
  index: number,
  flags: number,
): MdlTexture {
  const pixels = w * h
  const palBase = index + pixels
  const masked = (flags & STUDIO_NF_MASKED) !== 0
  const rgba = new Uint8Array(pixels * 4)
  for (let p = 0; p < pixels; p++) {
    const idx = dv.getUint8(index + p)
    const o = p * 4
    if (masked && idx === 255) continue
    const pal = palBase + idx * 3
    rgba[o] = dv.getUint8(pal)
    rgba[o + 1] = dv.getUint8(pal + 1)
    rgba[o + 2] = dv.getUint8(pal + 2)
    rgba[o + 3] = 255
  }
  return { name, width: w, height: h, rgba, transparent: masked }
}

function decodeTris(dv: DataView, start: number): MdlTriVert[] {
  const out: MdlTriVert[] = []
  let p = start
  for (;;) {
    const count = dv.getInt16(p, true)
    p += 2
    if (count === 0) break
    const fan = count < 0
    const n = Math.abs(count)
    const verts: MdlTriVert[] = new Array(n)
    for (let i = 0; i < n; i++) {
      verts[i] = {
        vert: dv.getInt16(p, true),
        s: dv.getInt16(p + 4, true),
        t: dv.getInt16(p + 6, true),
      }
      p += 8 // vert, norm, s, t (4 int16)
    }
    if (fan) {
      for (let i = 2; i < n; i++) out.push(verts[0], verts[i - 1], verts[i])
    } else {
      for (let i = 2; i < n; i++) {
        if (i % 2 === 0) out.push(verts[i - 2], verts[i - 1], verts[i])
        else out.push(verts[i - 1], verts[i - 2], verts[i])
      }
    }
  }
  return out
}

function parseModel(dv: DataView, o: number): MdlModel {
  const i32 = (x: number) => dv.getInt32(x, true)
  const nummesh = i32(o + 72)
  const meshindex = i32(o + 76)
  const numverts = i32(o + 80)
  const vertinfoindex = i32(o + 84)
  const vertindex = i32(o + 88)

  const vertices = new Float32Array(numverts * 3)
  for (let v = 0; v < numverts * 3; v++) {
    vertices[v] = dv.getFloat32(vertindex + v * 4, true)
  }
  const vertBones = new Uint8Array(numverts)
  for (let v = 0; v < numverts; v++) {
    vertBones[v] = dv.getUint8(vertinfoindex + v)
  }

  const meshes: MdlMesh[] = []
  for (let m = 0; m < nummesh; m++) {
    const mo = meshindex + m * 20
    meshes.push({ skinRef: i32(mo + 8), tris: decodeTris(dv, i32(mo + 4)) })
  }
  return { vertices, vertBones, meshes }
}

export function parseMdl(buffer: ArrayBuffer): Mdl {
  const dv = new DataView(buffer)
  const i32 = (o: number) => dv.getInt32(o, true)
  const f32 = (o: number) => dv.getFloat32(o, true)

  if (readStr(dv, 0, 4) !== 'IDST') throw new Error('Not a GoldSrc MDL (IDST)')
  if (i32(4) !== 10) throw new Error(`Unsupported MDL version ${i32(4)}`)

  const numbones = i32(140)
  const boneindex = i32(144)
  const numseq = i32(164)
  const seqindex = i32(168)
  const numtextures = i32(180)
  const textureindex = i32(184)
  const numskinref = i32(192)
  const skinindex = i32(200)
  const numbodyparts = i32(204)
  const bodypartindex = i32(208)
  const numattachments = i32(212)
  const attachmentindex = i32(216)

  const bones: MdlBone[] = []
  for (let i = 0; i < numbones; i++) {
    const o = boneindex + i * 112
    bones.push({
      name: readStr(dv, o, 32),
      parent: i32(o + 32),
      pos: [f32(o + 64), f32(o + 68), f32(o + 72)],
      rot: [f32(o + 76), f32(o + 80), f32(o + 84)],
      scale: [
        f32(o + 88),
        f32(o + 92),
        f32(o + 96),
        f32(o + 100),
        f32(o + 104),
        f32(o + 108),
      ],
    })
  }

  const sequences: MdlSequence[] = []
  for (let i = 0; i < numseq; i++) {
    const o = seqindex + i * 176
    sequences.push({
      label: readStr(dv, o, 32),
      fps: f32(o + 32),
      numFrames: i32(o + 56),
      numBlends: i32(o + 120),
      animIndex: i32(o + 124),
      blendStart: f32(o + 136),
      blendEnd: f32(o + 144),
      seqGroup: i32(o + 156),
    })
  }

  const textures: MdlTexture[] = []
  for (let i = 0; i < numtextures; i++) {
    const o = textureindex + i * 80
    textures.push(
      decodeTexture(
        dv,
        readStr(dv, o, 64),
        i32(o + 68),
        i32(o + 72),
        i32(o + 76),
        i32(o + 64),
      ),
    )
  }

  const skinRefs: number[] = []
  for (let i = 0; i < numskinref; i++) {
    skinRefs.push(dv.getInt16(skinindex + i * 2, true))
  }

  const models: MdlModel[] = []
  for (let b = 0; b < numbodyparts; b++) {
    const bo = bodypartindex + b * 76
    if (i32(bo + 64) > 0) models.push(parseModel(dv, i32(bo + 72)))
  }

  // Attachments (88 bytes each): char unused[32], i32 flags, i32 bone, vec3 org…
  const attachments: MdlAttachment[] = []
  for (let i = 0; i < numattachments; i++) {
    const o = attachmentindex + i * 88
    attachments.push({
      bone: i32(o + 36),
      org: [f32(o + 40), f32(o + 44), f32(o + 48)],
    })
  }

  return { bones, models, textures, skinRefs, sequences, attachments, view: dv }
}

/** Read an RLE-compressed studio anim channel at an integer frame. */
function animValueAtFrame(dv: DataView, base: number, frame: number): number {
  let k = frame
  let p = base
  let valid = dv.getUint8(p)
  let total = dv.getUint8(p + 1)
  while (total <= k) {
    k -= total
    p += (valid + 1) * 2
    valid = dv.getUint8(p)
    total = dv.getUint8(p + 1)
  }
  if (valid > k) return dv.getInt16(p + (k + 1) * 2, true)
  return dv.getInt16(p + valid * 2, true)
}

/** Bone local transforms for the given sequence at `frame` (no interpolation). */
export function sampleBoneLocals(
  mdl: Mdl,
  seqIndex: number,
  frame: number,
  blend = 0,
): BoneLocal[] {
  const out: BoneLocal[] = mdl.bones.map((b) => ({
    pos: [b.pos[0], b.pos[1], b.pos[2]],
    rot: [b.rot[0], b.rot[1], b.rot[2]],
  }))

  const seq = mdl.sequences[seqIndex]
  if (!seq || seq.seqGroup !== 0 || seq.animIndex <= 0) return out

  const dv = mdl.view
  const f = Math.max(0, Math.min(seq.numFrames - 1, Math.floor(frame)))
  const numBlends = Math.max(1, seq.numBlends)
  const blendStep = mdl.bones.length * 12
  const span = seq.blendEnd - seq.blendStart
  const blendFrame =
    numBlends > 1 && Math.abs(span) > 1e-6
      ? Math.max(
          0,
          Math.min(
            numBlends - 1,
            ((blend - seq.blendStart) / span) * (numBlends - 1),
          ),
        )
      : 0
  const b0 = Math.floor(blendFrame)
  const b1 = Math.min(numBlends - 1, b0 + 1)
  const bt = blendFrame - b0

  for (let b = 0; b < mdl.bones.length; b++) {
    const bone = mdl.bones[b]
    for (let j = 0; j < 6; j++) {
      const base0 = seq.animIndex + b0 * blendStep + b * 12
      const off0 = dv.getUint16(base0 + j * 2, true)
      if (off0 === 0) continue
      let delta = animValueAtFrame(dv, base0 + off0, f) * bone.scale[j]
      if (bt > 0) {
        const base1 = seq.animIndex + b1 * blendStep + b * 12
        const off1 = dv.getUint16(base1 + j * 2, true)
        if (off1 !== 0) {
          const delta1 = animValueAtFrame(dv, base1 + off1, f) * bone.scale[j]
          delta = delta * (1 - bt) + delta1 * bt
        }
      }
      if (j < 3) out[b].pos[j] += delta
      else out[b].rot[j - 3] += delta
    }
  }
  return out
}

/** Find a sequence index whose label contains `name` (case-insensitive). */
export function findSequence(mdl: Mdl, ...names: string[]): number {
  for (const name of names) {
    const idx = mdl.sequences.findIndex((s) =>
      s.label.toLowerCase().includes(name),
    )
    if (idx >= 0) return idx
  }
  return 0
}
